import { getCookie } from 'cookies-next'
import { OptionsType } from 'cookies-next/lib/types'
import jwtDecode from 'jwt-decode'
import { AUTH_JWT_COOKIE, AUTH_SESSION_COOKIE } from '@/constants/cookies'
import { logger } from './logging'
import { getStringOrEmptyString, getValidString } from './primitives/strings'
import { Maybe } from './types'

export interface DecodedJwt {
  /** The intended audience of the JWT. */
  aud: 'ellis_island' | string

  /** The issuer of the JWT. */
  iss: 'ellis_island' | string

  /** The receiver of the JWT. */
  azp: 'angel_web' | string

  /** JWT issued at date in seconds since the epoch. Multiply by 1000 to get ms when creating a Date object. */
  iat: number

  /** JWT expiration date in seconds since the epoch. Multiply by 1000 to get ms when creating a Date object. */
  exp: number

  jti?: string

  nbf?: number

  /** space delimited list of scopes allowed for this JWT. For example "openid profile email" */
  scope: string

  /** User id */
  sub: string

  /** subject type of the JWT. Currently always "user" */
  subject_type: 'user'

  /** JWT type. Currently always "access" */
  typ: 'access'
}

interface DecodedMasqueradeJwt extends DecodedJwt {
  is_masquerade?: boolean
  actor_uuid?: string
  actor_email?: string
  actor_first_name?: string | null
  actor_last_name?: string | null
  target_email?: string
  target_first_name?: string | null
  target_last_name?: string | null
}

export interface DecodedAccessToken extends DecodedMasqueradeJwt {
  userId: string
}

export function getAccessTokenFromClient(): string {
  return getStringOrEmptyString(getValidString(getCookie(AUTH_JWT_COOKIE)))
}

export function getJwtFromCookie(): DecodedAccessToken | null {
  const accessToken = getAccessTokenFromClient()
  return decodeAccessToken(accessToken)
}

export function isDecodedAccessToken(maybeAccessToken: unknown): maybeAccessToken is DecodedAccessToken {
  if (!maybeAccessToken || typeof maybeAccessToken !== 'object') return false
  const accessToken = maybeAccessToken as DecodedAccessToken
  return isDecodedJwt(accessToken) && typeof accessToken.userId === 'string' && accessToken.userId.length > 0
}

export function isDecodedJwt(maybeJwt: unknown): maybeJwt is DecodedJwt {
  if (!maybeJwt || typeof maybeJwt !== 'object') return false
  const jwt = maybeJwt as DecodedJwt
  // Only check the properties that we are actually using across the codebase:
  return typeof jwt.sub === 'string' && typeof jwt.exp === 'number' && typeof jwt.iat === 'number'
}

export function decodeAccessToken(accessToken: Maybe<string>): DecodedAccessToken | null {
  if (!accessToken) return null
  try {
    const decoded = accessToken ? jwtDecode<DecodedJwt>(accessToken) : null
    return decoded ? { ...decoded, userId: decoded.sub } : null
  } catch (error) {
    logger().warn('Error decoding access token', { error, accessToken })
    return null
  }
}

/**
 * This cookie contains the user's access token. Presence of this cookie indicates that the user is logged in.
 * @returns
 */
export function hasAuthJwtCookie(): boolean {
  return Boolean(getAccessTokenFromClient())
}

/**
 * The Auth Session Cookie is used to refetch the access token. This cookie should **NOT** be used to check if the user is logged in.
 * @param options
 * @returns
 */
export function hasAuthSessionCookie(options?: OptionsType): boolean {
  return Boolean(getCookie(AUTH_SESSION_COOKIE, options))
}
